import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { posts } from "@/mocks/community";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/topics", label: "话题榜" },
  { to: "/about", label: "关于我们" },
  { to: "/faq", label: "常见问题" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return posts
      .filter((post) =>
        [post.title, post.excerpt, post.categoryName, ...post.tags]
          .join(" ")
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 5);
  }, [keyword]);

  const linkBase = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-100 hover:text-accent-200";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg font-bold tracking-tight ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              爆料社区
            </span>
            <span
              className={`mt-0.5 text-[10px] tracking-[0.2em] ${
                solid ? "text-foreground-500" : "text-background-200"
              }`}
            >
              BAOLIAO COMMUNITY
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 md:flex" aria-label="主导航">
          {navLinks.map((link) => {
            const active = link.to === "/" ? isHome : location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? solid
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-50/20 text-background-50"
                    : linkBase
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="搜索话题"
            aria-expanded={searchOpen}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              solid
                ? "bg-background-100 text-foreground-700 hover:bg-background-200"
                : "bg-background-50/20 text-background-50 hover:bg-background-50/30"
            }`}
          >
            <i className={searchOpen ? "ri-close-line text-lg" : "ri-search-line text-lg"} aria-hidden="true" />
          </button>
          <Link
            to="/about#submit"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            我要爆料
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:hidden ${
              solid
                ? "bg-background-100 text-foreground-700"
                : "bg-background-50/20 text-background-50"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 shadow-sm md:px-6">
          <div className="mx-auto w-full max-w-3xl">
            <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-100 px-3">
              <i className="ri-search-line text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                autoFocus
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索话题、板块或关键词，例如「大小周」"
                className="w-full bg-transparent py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            {keyword.trim() && (
              <div className="mt-3 overflow-hidden rounded-lg border border-background-200">
                {results.length > 0 ? (
                  results.map((post) => (
                    <Link
                      key={post.id}
                      to={`/post/${post.id}`}
                      className="flex items-start gap-3 border-b border-background-100 bg-background-50 px-4 py-3 transition-colors last:border-0 hover:bg-primary-50"
                    >
                      <i className="ri-file-text-line mt-0.5 text-primary-500" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground-900">{post.title}</span>
                        <span className="mt-0.5 block text-xs text-foreground-500">
                          {post.categoryName} · 热度 {post.heat}
                        </span>
                      </span>
                    </Link>
                  ))
                ) : (
                  <p className="bg-background-50 px-4 py-6 text-center text-sm text-foreground-500">
                    没有找到相关话题，换个关键词试试。
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 shadow-sm md:hidden">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-md px-3 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/about#submit"
            className="mt-3 flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            我要爆料
          </Link>
        </div>
      )}
    </header>
  );
}